import React, { useState } from 'react';
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  Button,
  HStack,
  Icon,
  useColorModeValue,
  Badge,
  Grid,
  SimpleGrid,
  Stack,
  Image,
  useToast,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { demoConfig } from '../config/demoConfig';
import {
  FaDesktop,
  FaMobile,
  FaCheck,
  FaPlay,
  FaGamepad,
  FaDownload,
} from 'react-icons/fa';
import { launchPokerWithAutoLogin } from '../services/autoLoginService';

const PokerProductsPage: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [isLaunching, setIsLaunching] = useState(false);

  const handleLaunch = async (clientUrl: string) => {
    setIsLaunching(true);
    try {
      await launchPokerWithAutoLogin(clientUrl);
    } catch (error) {
      toast({
        title: t('pokerProductsPage.launchError') || 'Launch Failed',
        description:
          t('pokerProductsPage.launchErrorDesc') ||
          'Failed to launch client. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLaunching(false);
    }
  };

  return (
    <Box>
      <Box
        py={{ base: 12, md: 20 }}
        bg={useColorModeValue('gray.50', 'gray.900')}
      >
        <Container
          display='flex'
          flexDirection='column'
          alignItems='center'
          maxW='7xl'
        >
          <VStack
            spacing={{ base: 3, md: 4 }}
            textAlign='center'
            mb={{ base: 8, md: 12, lg: 16 }}
          >
            <Badge
              colorScheme='purple'
              variant='subtle'
              px={4}
              py={2}
              borderRadius='full'
              fontSize='sm'
              fontWeight='bold'
            >
              {t('pokerProductsPage.badge')}
            </Badge>
            <Heading
              size={{ base: 'lg', md: 'xl', lg: '2xl' }}
              color={useColorModeValue('gray.800', 'white')}
            >
              {t('pokerProductsPage.title')}
            </Heading>
            <Text
              fontSize={{ base: 'md', md: 'lg' }}
              color='gray.500'
              maxW='2xl'
              px={{ base: 4, md: 0 }}
            >
              {t('pokerProductsPage.subtitle')}
            </Text>
          </VStack>

          <VStack spacing={{ base: 8, md: 12, lg: 16 }}>
            {/* Desktop Version */}
            <Grid
              templateColumns={{ base: '1fr', lg: '480px 1fr' }}
              gap={{ base: 6, md: 8, lg: 12 }}
              alignItems='center'
              w='full'
            >
              <Box
                bg={useColorModeValue('white', 'gray.800')}
                borderRadius='2xl'
                p={{ base: 6, md: 8 }}
                boxShadow='xl'
                border='1px solid'
                borderColor={useColorModeValue('gray.200', 'gray.700')}
                _hover={{
                  transform: 'translateY(-4px)',
                  boxShadow: '2xl',
                }}
                transition='all 0.3s ease'
                maxW={{ base: '100%', lg: '480px' }}
                w='full'
              >
                <VStack align='start' spacing={{ base: 4, md: 6 }}>
                  <VStack align='start' spacing={{ base: 3, md: 4 }}>
                    <HStack spacing={{ base: 3, md: 4 }}>
                      <Box
                        p={4}
                        bg={useColorModeValue('purple.100', 'purple.200')}
                        borderRadius='2xl'
                        color={useColorModeValue('purple.600', 'purple.800')}
                      >
                        <Icon as={FaDesktop} boxSize={{ base: 8, md: 10 }} />
                      </Box>
                      <VStack align='start' spacing={2}>
                        <Heading
                          size={{ base: 'lg', md: 'xl' }}
                          color={useColorModeValue('gray.800', 'white')}
                          fontWeight='800'
                        >
                          {t('pokerProductsPage.desktop.title')}
                        </Heading>
                        <HStack spacing={{ base: 1, md: 2 }} flexWrap='wrap'>
                          <Badge
                            colorScheme='purple'
                            variant='solid'
                            borderRadius='full'
                          >
                            {t('pokerProductsPage.desktop.badges.professional')}
                          </Badge>
                          <Badge
                            colorScheme='green'
                            variant='outline'
                            borderRadius='full'
                          >
                            {t('pokerProductsPage.desktop.badges.fullFeatured')}
                          </Badge>
                        </HStack>
                      </VStack>
                    </HStack>

                    <Text
                      fontSize={{ base: 'md', md: 'lg' }}
                      color={useColorModeValue('gray.600', 'gray.300')}
                      lineHeight='tall'
                    >
                      {t('pokerProductsPage.desktop.description')}
                    </Text>
                  </VStack>
                  {/* Image inside card - only on mobile */}
                  <Box
                    display={{ base: 'block', lg: 'none' }}
                    w='full'
                    mt={4}
                    borderRadius='xl'
                    overflow='hidden'
                  >
                    <Image
                      src='/images/games/texas-holdem/desktop-table.png'
                      alt='Desktop Poker Table Interface'
                      w='full'
                      h='auto'
                      objectFit='contain'
                    />
                  </Box>
                  <VStack align='start' spacing={4} w='full'>
                    <Heading
                      size='md'
                      color={useColorModeValue('gray.800', 'white')}
                      fontWeight='700'
                    >
                      {t('pokerProductsPage.desktop.featuresTitle')}
                    </Heading>

                    <SimpleGrid
                      columns={2}
                      spacing={{ base: 2, md: 3 }}
                      w='full'
                    >
                      <HStack spacing={3}>
                        <Icon
                          as={FaCheck}
                          color='green.500'
                          boxSize={{ base: 4, md: 5 }}
                        />
                        <Text
                          fontSize={{ base: 'xs', md: 'sm' }}
                          fontWeight='500'
                          color={useColorModeValue('gray.700', 'gray.300')}
                        >
                          {t('pokerProductsPage.desktop.features.multiTable')}
                        </Text>
                      </HStack>
                      <HStack spacing={3}>
                        <Icon
                          as={FaCheck}
                          color='green.500'
                          boxSize={{ base: 4, md: 5 }}
                        />
                        <Text
                          fontSize={{ base: 'xs', md: 'sm' }}
                          fontWeight='500'
                          color={useColorModeValue('gray.700', 'gray.300')}
                        >
                          {t('pokerProductsPage.desktop.features.analytics')}
                        </Text>
                      </HStack>
                      <HStack spacing={3}>
                        <Icon
                          as={FaCheck}
                          color='green.500'
                          boxSize={{ base: 4, md: 5 }}
                        />
                        <Text
                          fontSize={{ base: 'xs', md: 'sm' }}
                          fontWeight='500'
                          color={useColorModeValue('gray.700', 'gray.300')}
                        >
                          {t(
                            'pokerProductsPage.desktop.features.professionalTools'
                          )}
                        </Text>
                      </HStack>
                      <HStack spacing={3}>
                        <Icon
                          as={FaCheck}
                          color='green.500'
                          boxSize={{ base: 4, md: 5 }}
                        />
                        <Text
                          fontSize={{ base: 'xs', md: 'sm' }}
                          fontWeight='500'
                          color={useColorModeValue('gray.700', 'gray.300')}
                        >
                          {t('pokerProductsPage.desktop.features.customRules')}
                        </Text>
                      </HStack>
                      <HStack spacing={3}>
                        <Icon
                          as={FaCheck}
                          color='green.500'
                          boxSize={{ base: 4, md: 5 }}
                        />
                        <Text
                          fontSize={{ base: 'xs', md: 'sm' }}
                          fontWeight='500'
                          color={useColorModeValue('gray.700', 'gray.300')}
                        >
                          {t('pokerProductsPage.desktop.features.realTimeChat')}
                        </Text>
                      </HStack>
                      <HStack spacing={3}>
                        <Icon
                          as={FaCheck}
                          color='green.500'
                          boxSize={{ base: 4, md: 5 }}
                        />
                        <Text
                          fontSize={{ base: 'xs', md: 'sm' }}
                          fontWeight='500'
                          color={useColorModeValue('gray.700', 'gray.300')}
                        >
                          {t(
                            'pokerProductsPage.desktop.features.adminDashboard'
                          )}
                        </Text>
                      </HStack>
                    </SimpleGrid>
                  </VStack>

                  <Stack
                    direction={{ base: 'column', sm: 'row' }}
                    spacing={4}
                    w='full'
                  >
                    <Button
                      onClick={() => handleLaunch(demoConfig.pokerDesktop)}
                      isLoading={isLaunching}
                      loadingText={
                        t('pokerProductsPage.desktop.buttons.launching') ||
                        'Launching...'
                      }
                      size='lg'
                      colorScheme='purple'
                      rightIcon={<FaPlay />}
                      flex={1}
                      borderRadius='xl'
                      _hover={{
                        transform: 'translateY(-2px)',
                        boxShadow: 'xl',
                      }}
                      transition='all 0.3s'
                    >
                      {t('pokerProductsPage.desktop.buttons.launch')}
                    </Button>
                  </Stack>
                </VStack>
              </Box>

              <Box
                position='relative'
                borderRadius='2xl'
                overflow='hidden'
                _hover={{
                  transform: 'scale(1.02)',
                  boxShadow: '4xl',
                }}
                transition='all 0.3s'
                w='full'
                maxW='100%'
                h='auto'
                display={{ base: 'none', lg: 'block' }}
              >
                <Image
                  src='/images/games/texas-holdem/desktop-table.png'
                  alt='Desktop Poker Table Interface'
                  w='full'
                  h='auto'
                  objectFit='contain'
                />
              </Box>
            </Grid>

            {/* Mobile Version */}
            <Grid
              templateColumns={{ base: '1fr', lg: '1fr 1fr' }}
              gap={{ base: 6, md: 8, lg: 12 }}
              alignItems='center'
              w='full'
            >
              <Box
                bg={useColorModeValue('white', 'gray.800')}
                borderRadius='2xl'
                p={{ base: 6, md: 8 }}
                boxShadow='xl'
                border='1px solid'
                borderColor={useColorModeValue('gray.200', 'gray.700')}
                _hover={{
                  transform: 'translateY(-4px)',
                  boxShadow: '2xl',
                }}
                transition='all 0.3s ease'
                maxW={{ base: '100%', lg: '480px' }}
                w='full'
              >
                <VStack align='start' spacing={{ base: 4, md: 6 }}>
                  <VStack align='start' spacing={{ base: 3, md: 4 }}>
                    <HStack spacing={{ base: 3, md: 4 }}>
                      <Box
                        p={4}
                        bg={useColorModeValue('blue.100', 'blue.200')}
                        borderRadius='2xl'
                        color={useColorModeValue('blue.600', 'blue.800')}
                      >
                        <Icon as={FaMobile} boxSize={{ base: 8, md: 10 }} />
                      </Box>
                      <VStack align='start' spacing={2}>
                        <Heading
                          size={{ base: 'lg', md: 'xl' }}
                          color={useColorModeValue('gray.800', 'white')}
                          fontWeight='800'
                        >
                          {t('pokerProductsPage.mobile.title')}
                        </Heading>
                        <HStack spacing={{ base: 1, md: 2 }} flexWrap='wrap'>
                          <Badge
                            colorScheme='blue'
                            variant='solid'
                            borderRadius='full'
                          >
                            {t(
                              'pokerProductsPage.mobile.badges.touchOptimized'
                            )}
                          </Badge>
                          <Badge
                            colorScheme='orange'
                            variant='outline'
                            borderRadius='full'
                          >
                            {t('pokerProductsPage.mobile.badges.onTheGo')}
                          </Badge>
                        </HStack>
                      </VStack>
                    </HStack>

                    <Text
                      fontSize={{ base: 'md', md: 'lg' }}
                      color={useColorModeValue('gray.600', 'gray.300')}
                      lineHeight='tall'
                    >
                      {t('pokerProductsPage.mobile.description')}
                    </Text>
                  </VStack>
                  {/* Images inside card - only on mobile */}
                  <HStack
                    spacing={4}
                    w='full'
                    mt={4}
                    display={{ base: 'flex', lg: 'none' }}
                    justify='center'
                  >
                    <Box
                      borderRadius='xl'
                      overflow='hidden'
                      flex={1}
                      maxW='200px'
                    >
                      <Image
                        src='/images/games/texas-holdem/mobile-table.png'
                        alt='Mobile Poker Table Interface'
                        w='full'
                        h='auto'
                        objectFit='contain'
                      />
                    </Box>
                    <Box
                      borderRadius='xl'
                      overflow='hidden'
                      flex={1}
                      maxW='200px'
                    >
                      <Image
                        src='/images/games/texas-holdem/mobile-table2.png'
                        alt='Mobile Poker Table Interface - Alternative View'
                        w='full'
                        h='auto'
                        objectFit='contain'
                      />
                    </Box>
                  </HStack>
                  <VStack align='start' spacing={4} w='full'>
                    <Heading
                      size='md'
                      color={useColorModeValue('gray.800', 'white')}
                      fontWeight='700'
                    >
                      {t('pokerProductsPage.mobile.featuresTitle')}
                    </Heading>

                    <SimpleGrid
                      columns={2}
                      spacing={{ base: 2, md: 3 }}
                      w='full'
                    >
                      <HStack spacing={3}>
                        <Icon
                          as={FaCheck}
                          color='green.500'
                          boxSize={{ base: 4, md: 5 }}
                        />
                        <Text
                          fontSize={{ base: 'xs', md: 'sm' }}
                          fontWeight='500'
                          color={useColorModeValue('gray.700', 'gray.300')}
                        >
                          {t('pokerProductsPage.mobile.features.touchControls')}
                        </Text>
                      </HStack>
                      <HStack spacing={3}>
                        <Icon
                          as={FaCheck}
                          color='green.500'
                          boxSize={{ base: 4, md: 5 }}
                        />
                        <Text
                          fontSize={{ base: 'xs', md: 'sm' }}
                          fontWeight='500'
                          color={useColorModeValue('gray.700', 'gray.300')}
                        >
                          {t(
                            'pokerProductsPage.mobile.features.responsiveDesign'
                          )}
                        </Text>
                      </HStack>
                      <HStack spacing={3}>
                        <Icon
                          as={FaCheck}
                          color='green.500'
                          boxSize={{ base: 4, md: 5 }}
                        />
                        <Text
                          fontSize={{ base: 'xs', md: 'sm' }}
                          fontWeight='500'
                          color={useColorModeValue('gray.700', 'gray.300')}
                        >
                          {t('pokerProductsPage.mobile.features.quickPlay')}
                        </Text>
                      </HStack>
                      <HStack spacing={3}>
                        <Icon
                          as={FaCheck}
                          color='green.500'
                          boxSize={{ base: 4, md: 5 }}
                        />
                        <Text
                          fontSize={{ base: 'xs', md: 'sm' }}
                          fontWeight='500'
                          color={useColorModeValue('gray.700', 'gray.300')}
                        >
                          {t(
                            'pokerProductsPage.mobile.features.offlinePractice'
                          )}
                        </Text>
                      </HStack>
                      <HStack spacing={3}>
                        <Icon
                          as={FaCheck}
                          color='green.500'
                          boxSize={{ base: 4, md: 5 }}
                        />
                        <Text
                          fontSize={{ base: 'xs', md: 'sm' }}
                          fontWeight='500'
                          color={useColorModeValue('gray.700', 'gray.300')}
                        >
                          {t(
                            'pokerProductsPage.mobile.features.pushNotifications'
                          )}
                        </Text>
                      </HStack>
                      <HStack spacing={3}>
                        <Icon
                          as={FaCheck}
                          color='green.500'
                          boxSize={{ base: 4, md: 5 }}
                        />
                        <Text
                          fontSize={{ base: 'xs', md: 'sm' }}
                          fontWeight='500'
                          color={useColorModeValue('gray.700', 'gray.300')}
                        >
                          {t('pokerProductsPage.mobile.features.crossPlatform')}
                        </Text>
                      </HStack>
                    </SimpleGrid>
                  </VStack>

                  <Stack
                    direction={{ base: 'column', sm: 'row' }}
                    spacing={4}
                    w='full'
                  >
                    <Button
                      onClick={() => handleLaunch(demoConfig.pokerMobile)}
                      isLoading={isLaunching}
                      loadingText={
                        t('pokerProductsPage.mobile.buttons.launching') ||
                        'Launching...'
                      }
                      size='lg'
                      colorScheme='blue'
                      rightIcon={<FaGamepad />}
                      flex={1}
                      borderRadius='xl'
                      _hover={{
                        transform: 'translateY(-2px)',
                        boxShadow: 'xl',
                      }}
                      transition='all 0.3s'
                    >
                      {t('pokerProductsPage.mobile.buttons.play')}
                    </Button>
                    <Button
                      as={RouterLink}
                      to='/demo'
                      size='lg'
                      variant='outline'
                      colorScheme='blue'
                      rightIcon={<FaDownload />}
                      flex={1}
                      borderRadius='xl'
                      _hover={{
                        transform: 'translateY(-2px)',
                        boxShadow: 'lg',
                      }}
                      transition='all 0.3s'
                    >
                      {t('pokerProductsPage.mobile.buttons.getApp')}
                    </Button>
                  </Stack>
                </VStack>
              </Box>

              <HStack
                spacing={{ base: 4, md: 6, lg: 8 }}
                align='center'
                justify='center'
                flexWrap={{ base: 'wrap', lg: 'nowrap' }}
                display={{ base: 'none', lg: 'flex' }}
                w='full'
              >
                <Box
                  position='relative'
                  borderRadius='3xl'
                  overflow='hidden'
                  _hover={{
                    transform: 'scale(1.02)',
                    boxShadow: '4xl',
                  }}
                  transition='all 0.3s'
                  display='flex'
                  alignItems='center'
                  justifyContent='center'
                  minH={{ base: '400px', md: '500px' }}
                  boxShadow='2xl'
                  flex='1'
                  maxW={{ base: '250px', xl: '300px' }}
                >
                  <Image
                    src='/images/games/texas-holdem/mobile-table.png'
                    alt='Mobile Poker Table Interface'
                    maxH={{ base: '500px', md: '680px' }}
                    w='100%'
                    h='auto'
                    objectFit='contain'
                    borderRadius='2xl'
                  />
                </Box>

                <Box
                  position='relative'
                  borderRadius='3xl'
                  overflow='hidden'
                  _hover={{
                    transform: 'scale(1.02)',
                    boxShadow: '4xl',
                  }}
                  transition='all 0.3s'
                  display='flex'
                  alignItems='center'
                  justifyContent='center'
                  minH={{ base: '400px', md: '500px' }}
                  boxShadow='2xl'
                  flex='1'
                  maxW={{ base: '250px', xl: '300px' }}
                >
                  <Image
                    src='/images/games/texas-holdem/mobile-table2.png'
                    alt='Mobile Poker Table Interface - Alternative View'
                    maxH={{ base: '500px', md: '680px' }}
                    w='100%'
                    h='auto'
                    objectFit='contain'
                    borderRadius='2xl'
                  />
                </Box>
              </HStack>
            </Grid>
          </VStack>
        </Container>
      </Box>
    </Box>
  );
};

export default PokerProductsPage;
